<script setup lang="ts">
  import { ref, computed, inject, provide, type Ref, onMounted, onUnmounted, watch } from 'vue'
  import { useVuelidate } from '@vuelidate/core'
  import { useQuestionnaireStore } from '@/store/questionnaireStore'
  import FieldGroup from './formElements/fieldGroup.vue'
  import FieldHelp from '@/components/questionaire/formElements/fieldHelp.vue'
  import FormResult from '@/components/questionaire/formElements/formResultV3.vue'
  import { Button } from '@/components/ui/button'
  import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
  import { useThemeColor } from '@/composables/useThemeColorV3'
  import { useTranslation } from '@/composables/useTranslation'
  import { type ClientQuestionnaireSubmission } from '@/types/generated'

  const { t } = useTranslation({
    en: {
      previous: 'Previous',
      next: 'Next',
      showMore: 'Show more',
      showLess: 'Show less'
    },
    de: {
      previous: 'Zurück',
      next: 'Weiter',
      showMore: 'Mehr anzeigen',
      showLess: 'Weniger anzeigen'
    }
  })

  const themeColor = computed(() => useThemeColor().value)

  const props = defineProps({
    formFields: {
      type: Array,
      default: () => []
    },
    metaData: {
      type: Object,
      default: () => ({})
    },
    rawSchema: {
      type: Array,
      default: () => []
    },
    formState: {
      type: Object,
      default: () => ({})
    },
    fieldValidationErrors: {
      type: Object,
      default: () => ({})
    },
    validateField: Function,
    handleFieldValidationError: Function,
    saveFieldData: Function,
    onSubmit: Function,
    back: Function,
    next: Function,
    jumpToField: Function,
    setToLastField: Function,
    getFieldByUuid: Function,
    handleFieldChange: Function
  })

  const v = useVuelidate({ $stopPropagation: true })
  const store = useQuestionnaireStore()

  const autoSaveTimeout = ref<number | null>(null)
  const isHelpTextExpanded = ref(false)

  const activeField = computed(() => {
    // Use the fields from props (which have components) instead of store
    const field = props.formFields?.find((f: any) => f.uuid === props.formState.activeFieldId)
    return field || ({} as any)
  })

  // Reset expansion state when active field changes
  watch(
    () => props.formState.activeFieldId,
    () => {
      isHelpTextExpanded.value = false
    }
  )

  const helpTextRef = ref<HTMLElement | null>(null)

  const isHelpTextTruncated = computed(() => {
    if (!activeField.value.help) return false
    // Check if text would be truncated (rough estimation: 3 lines ~= 70px)
    const text = activeField.value.help
    // If text is longer than ~150 characters, it's likely to be truncated
    return text.length > 150
  })

  function toggleHelpText() {
    isHelpTextExpanded.value = !isHelpTextExpanded.value
  }

  const submission = inject<Ref<ClientQuestionnaireSubmission | null>>('questionnaire', ref(null))

  provide(
    'rawSchema',
    computed(() => props.rawSchema)
  )

  const processChange = async (uuid: string, value: any) => {
    props.handleFieldChange?.(uuid, value)

    if (props.metaData.isPreview) {
      return
    }

    await props.saveFieldData?.(uuid, value, submission.value)
  }

  function handleNext() {
    const currentField = activeField.value
    const currentValue = store.formData[currentField.uuid]

    if (
      currentField &&
      !currentField.optional &&
      (currentValue === undefined ||
        currentValue === null ||
        currentValue === '' ||
        (Array.isArray(currentValue) && currentValue.length === 0))
    ) {
      handleFieldValidationErrorLocal(currentField.uuid, true)
      return
    }

    if (props.formState.valid) {
      processChange(props.formState.activeFieldId, store.formData[props.formState.activeFieldId])
    }

    props.onSubmit?.()
  }

  function handleFieldValidationErrorLocal(fieldId: string, hasError: boolean) {
    props.handleFieldValidationError?.(fieldId, hasError)
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()

      if (props.formState.valid) {
        handleNext()
      }
    }
  }

  onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
  })

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
  })
</script>

<template>
  <div class="flex h-full w-full flex-col">
    <div class="flex flex-1">
      <div class="flex flex-1 flex-col transition-all duration-300 ease-in-out">
        <div class="flex flex-1 flex-col items-center justify-center p-4">
          <template v-if="!formState.isLastField">
            <FieldGroup
              :fieldId="formState.activeFieldId"
              :activeField="formState.activeFieldId"
              :next="formState.next"
              class="w-full max-w-[560px]"
            >
              <div
                class="flex min-h-[360px] flex-col justify-between rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
              >
                <div class="flex flex-col">
                  <div class="flex flex-col gap-1">
                    <h2
                      class="text-lg font-medium leading-tight text-gray-900"
                      :data-cy="`QuestionnaireTemplateV3QuestionTitle-${activeField.uuid}`"
                    >
                      {{ activeField.label }}
                    </h2>
                    <div
                      v-if="activeField.help"
                      class="flex flex-col gap-1"
                    >
                      <div
                        ref="helpTextRef"
                        :class="[
                          'text-sm font-normal leading-tight text-gray-900 transition-all duration-200',
                          !isHelpTextExpanded && isHelpTextTruncated ? 'line-clamp-3' : ''
                        ]"
                      >
                        <FieldHelp :help-text="activeField.help" />
                      </div>
                      <button
                        v-if="isHelpTextTruncated"
                        class="self-start text-sm font-medium text-purple-600 underline transition-colors hover:text-purple-800"
                        type="button"
                        @click="toggleHelpText"
                      >
                        {{ isHelpTextExpanded ? t('showLess') : t('showMore') }}
                      </button>
                    </div>
                  </div>

                  <div class="w-full">
                    <Component
                      :is="activeField.component"
                      v-model="store.formData[activeField.uuid]"
                      :field="activeField"
                      :is-preview-mode="metaData.isPreview"
                      @files-changed="
                        processChange(activeField.uuid, store.formData[activeField.uuid])
                      "
                      @validation-error="
                        (hasError: boolean) =>
                          handleFieldValidationErrorLocal(activeField.uuid, hasError)
                      "
                    />
                  </div>
                </div>

                <div class="flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    class="flex h-8 items-center justify-center gap-1.5 rounded-md px-2 text-sm font-medium shadow-sm"
                    :class="`focus-visible:ring-${themeColor}-600`"
                    data-cy="QuestionnaireTemplateV3PreviousButton"
                    @click="back"
                  >
                    <ChevronLeft class="h-5 w-5" />
                    <span>{{ t('previous') }}</span>
                  </Button>
                  <Button
                    :class="[
                      `flex h-8 w-[100px] items-center justify-center gap-1.5 rounded-md px-2 text-sm font-medium
                      text-white shadow-sm bg-${themeColor}-600 focus-visible:ring-${themeColor}-600`,
                      props.formState.valid ? `hover:bg-${themeColor}-700` : 'opacity-50'
                    ]"
                    :disabled="!props.formState.valid"
                    data-cy="QuestionnaireTemplateV3NextButton"
                    @click="handleNext"
                  >
                    <span>{{ t('next') }}</span>
                    <ChevronRight class="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </FieldGroup>
          </template>
          <FormResult
            v-if="formState.isLastField"
            :isLastField="formState.isLastField"
            :form-fields="formFields"
            :is-external="metaData.isExternal"
            :is-preview-mode="metaData.isPreview"
            @cell-click="jumpToField"
          />
        </div>
      </div>
    </div>
  </div>
</template>
